import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageLayout, Container } from '../components/Layout';
import ProductCard from '../components/ProductCard';
import { Button, EmptyState, PageLoader } from '../components/UI';
import api, { imgUrl } from '../utils/api';

/* ── Hero ─────────────────────────────────────────────────── */
const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 60%, var(--primary-light) 100%);
  color: #fff; padding: 90px 0; position: relative; overflow: hidden;
  &::before { content: ''; position: absolute; inset: 0; background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"); }
`;
const HeroContent = styled.div` position: relative; max-width: 600px; `;

/* ── Features ─────────────────────────────────────────────── */
const FeaturesBar = styled.div`
  background: #fff; border-bottom: 1px solid var(--gray-100);
  display: grid; grid-template-columns: repeat(4, 1fr);
  @media(max-width:768px) { grid-template-columns: repeat(2, 1fr); }
`;
const FeatureItem = styled.div`
  padding: 20px; text-align: center; border-right: 1px solid var(--gray-100);
  &:last-child { border-right: none; }
`;

/* ── Grid ────────────────────────────────────────────────── */
const ProductsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 20px;
  @media(max-width:480px) { grid-template-columns: repeat(2, 1fr); gap: 12px; }
`;
const CatsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 16px;
`;

const FEATURES = [
  { icon:'🚚', title:'Fast Delivery', sub:'Tracked shipping' },
  { icon:'🔒', title:'Secure Payments', sub:'PayPal protected' },
  { icon:'↩️', title:'Easy Returns', sub:'Hassle-free policy' },
  { icon:'💬', title:'24/7 Support', sub:'Always here to help' },
];

export default function Home() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/categories').then(d => setCategories(d.categories || [])),
      api.get('/products?featured=1&limit=8').then(d => setFeatured(d.products || [])),
      api.get('/products?limit=8').then(d => setNewArrivals(d.products || [])),
      api.get('/banners').then(d => setBanners(d.banners || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <Hero>
        <Container>
          <HeroContent>
            <h1 style={{ color:'#fff', fontSize:'clamp(2rem,5vw,3.5rem)', marginBottom:16, lineHeight:1.15 }}>
              Discover Quality Products for Every Lifestyle
            </h1>
            <p style={{ fontSize:'1.1rem', opacity:.9, marginBottom:32 }}>
              Shop from our curated collection with fast delivery, secure PayPal checkout, and exceptional customer care.
            </p>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <Button $variant="white" $size="lg" onClick={() => navigate('/shop')}>Shop Now →</Button>
              <Button onClick={() => navigate('/shop?featured=1')} style={{ background:'transparent', borderColor:'rgba(255,255,255,.5)', color:'#fff' }}>Featured Items</Button>
            </div>
          </HeroContent>
        </Container>
      </Hero>

      {/* Features bar */}
      <FeaturesBar>
        {FEATURES.map(f => (
          <FeatureItem key={f.title}>
            <div style={{ fontSize:'1.5rem', marginBottom:6 }}>{f.icon}</div>
            <div style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--gray-800)' }}>{f.title}</div>
            <div style={{ fontSize:'0.78rem', color:'var(--gray-500)' }}>{f.sub}</div>
          </FeatureItem>
        ))}
      </FeaturesBar>

      {/* Categories */}
      <section style={{ padding:'60px 0', background:'#fff' }}>
        <Container>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
            <h2>Shop by Category</h2>
            <Link to="/shop"><Button $variant="outline" $size="sm">All Categories</Button></Link>
          </div>
          {loading ? <PageLoader /> : (
            <CatsGrid>
              {categories.slice(0, 8).map(c => (
                <Link key={c.id} to={`/shop?category=${c.slug}`}
                  style={{ display:'block', textAlign:'center', padding:'20px 12px', background:'var(--gray-50)', borderRadius:'var(--radius-lg)', border:'1px solid var(--gray-100)', transition:'var(--transition)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary-50)'; e.currentTarget.style.borderColor = 'var(--primary-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--gray-50)'; e.currentTarget.style.borderColor = 'var(--gray-100)'; }}>
                  <div style={{ fontSize:'2rem', marginBottom:8 }}>{c.icon || '🛍️'}</div>
                  <div style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--gray-800)' }}>{c.name}</div>
                </Link>
              ))}
            </CatsGrid>
          )}
        </Container>
      </section>

      {/* Featured Products */}
      <section style={{ padding:'60px 0' }}>
        <Container>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
            <h2>Featured Products</h2>
            <Link to="/shop?featured=1"><Button $variant="outline" $size="sm">View All</Button></Link>
          </div>
          {loading ? <PageLoader /> : featured.length
            ? <ProductsGrid>{featured.map(p => <ProductCard key={p.id} product={p} />)}</ProductsGrid>
            : <EmptyState icon="🛍️" title="No featured products yet" />}
        </Container>
      </section>

      {/* CTA Banner */}
      <section style={{ background:'linear-gradient(135deg,var(--primary-dark),var(--primary))', padding:'60px 0' }}>
        <Container style={{ textAlign:'center' }}>
          <h2 style={{ color:'#fff', marginBottom:12 }}>Free Shipping on Orders Over $50</h2>
          <p style={{ color:'rgba(255,255,255,.85)', marginBottom:28, fontSize:'1.05rem' }}>
            Use code <strong>FREESHIP</strong> at checkout. Limited time offer!
          </p>
          <Button $variant="white" $size="lg" onClick={() => navigate('/shop')}>Shop Now</Button>
        </Container>
      </section>

      {/* New Arrivals */}
      <section style={{ padding:'60px 0', background:'#fff' }}>
        <Container>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
            <h2>New Arrivals</h2>
            <Link to="/shop"><Button $variant="outline" $size="sm">See All</Button></Link>
          </div>
          {loading ? <PageLoader /> : newArrivals.length
            ? <ProductsGrid>{newArrivals.map(p => <ProductCard key={p.id} product={p} />)}</ProductsGrid>
            : <EmptyState icon="🆕" title="No products yet" />}
        </Container>
      </section>
    </PageLayout>
  );
}
